<!-- charts/RothConversionChart.vue - Integration with your existing planning system -->
<template>
  <div class="roth-conversion-chart">
    <div class="chart-header">
      <h3>🔄 Roth Conversion Analysis</h3>
      <div class="chart-controls">
        <button 
          @click="showWizard = true"
          class="btn btn-primary"
        >
          Launch Full Analysis
        </button>
      </div>
    </div>

    <!-- Quick Preview -->
    <div v-if="!showWizard" class="quick-preview">
      <div class="preview-cards">
        <div class="preview-card" :class="quickDecisionClass">
          <div class="card-header">
            <div class="card-icon">{{ quickDecisionIcon }}</div>
            <div class="card-title">
              <h4>{{ quickDecisionTitle }}</h4>
              <p>{{ quickDecisionDescription }}</p>
            </div>
          </div>
          <div class="card-metrics">
            <div class="metric">
              <span class="metric-label">Estimated Benefit:</span>
              <span class="metric-value" :class="benefitClass">{{ formatCurrency(estimatedBenefit) }}</span>
            </div>
            <div class="metric">
              <span class="metric-label">Tax Cost:</span>
              <span class="metric-value">{{ formatCurrency(estimatedTaxCost) }}</span>
            </div>
          </div>
        </div>

        <div class="preview-card info">
          <div class="card-header">
            <div class="card-icon">📊</div>
            <div class="card-title">
              <h4>Your Tax Situation</h4>
              <p>Current vs. expected retirement rates</p>
            </div>
          </div>
          <div class="tax-rate-comparison">
            <div class="rate-item">
              <span class="rate-label">Current Rate:</span>
              <span class="rate-value">{{ formatPercentage(currentEffectiveRate) }}</span>
            </div>
            <div class="rate-item">
              <span class="rate-label">Retirement Rate:</span>
              <span class="rate-value">{{ formatPercentage(retirementEffectiveRate) }}</span>
            </div>
            <div class="rate-item difference" :class="rateDifferenceClass">
              <span class="rate-label">Difference:</span>
              <span class="rate-value">{{ formatPercentage(Math.abs(rateDifference)) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="quick-actions">
        <div class="action-item">
          <h5>🎯 Quick Optimization</h5>
          <p>Find the optimal conversion amount for your situation.</p>
          <button @click="runQuickOptimization" class="btn btn-outline">
            Quick Optimize
          </button>
        </div>

        <div class="action-item">
          <h5>📈 Range Analysis</h5>
          <p>See how different conversion amounts affect your outcome.</p>
          <button @click="showRangeAnalysis = true" class="btn btn-outline">
            View Range
          </button>
        </div>

        <div class="action-item">
          <h5>📋 Full Analysis</h5>
          <p>Comprehensive Roth conversion planning with all considerations.</p>
          <button @click="showWizard = true" class="btn btn-accent">
            Complete Analysis
          </button>
        </div>
      </div>
    </div>

    <!-- Range Analysis (when toggled) -->
    <div v-if="showRangeAnalysis && !showWizard" class="range-analysis-section">
      <div class="section-header">
        <h4>Conversion Amount Analysis</h4>
        <button @click="showRangeAnalysis = false" class="btn-close">×</button>
      </div>
      <RothRangeChart
        :range-data="quickRangeData"
        :current-amount="defaultConversionAmount"
        :format-currency="formatCurrency"
        @update-amount="updateQuickAmount"
      />
    </div>

    <!-- Full Wizard Modal -->
    <div v-if="showWizard" class="wizard-modal">
      <div class="modal-backdrop" @click="showWizard = false"></div>
      <div class="modal-content">
        <RothConversionWizard
          :format-currency="formatCurrency"
          :personal-info="personalInfo"
          :assumptions="assumptions"
          @close="showWizard = false"
        />
        <button @click="showWizard = false" class="modal-close-btn">×</button>
      </div>
    </div>

    <!-- Integration Help -->
    <div class="integration-info">
      <details>
        <summary>ℹ️ About Roth Conversion Analysis</summary>
        <div class="info-content">
          <p><strong>What it does:</strong> Analyzes whether converting traditional retirement account funds to Roth accounts makes financial sense for your situation.</p>
          
          <h6>Key Factors Considered:</h6>
          <ul>
            <li>Current vs. future tax rates</li>
            <li>Required minimum distribution (RMD) reduction</li>
            <li>State tax arbitrage opportunities</li>
            <li>Medicare IRMAA impact</li>
            <li>Time horizon and investment growth</li>
          </ul>

          <h6>Data Sources:</h6>
          <ul>
            <li>Uses your personal information from the planning section</li>
            <li>Integrates with current income and expense projections</li>
            <li>Applies 2025 tax brackets and regulations</li>
          </ul>
        </div>
      </details>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRothCalculations } from '../roth/useRothCalculations.js';
import RothConversionWizard from '../roth/RothConversionWizard.vue';
import RothRangeChart from '../roth/charts/RothRangeChart.vue';

export default {
  name: 'RothConversionChart',
  components: {
    RothConversionWizard,
    RothRangeChart
  },
  props: {
    yearRows: {
      type: Array,
      default: () => []
    },
    items: {
      type: Array,
      default: () => []
    },
    formatCurrency: {
      type: Function,
      required: true
    },
    dollarType: {
      type: String,
      default: 'today'
    },
    personalInfo: {
      type: Object,
      default: () => ({})
    },
    assumptions: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const showWizard = ref(false);
    const showRangeAnalysis = ref(false);
    
    // Initialize Roth calculations with data from props
    const {
      currentBalance,
      conversionAmount,
      currentAge,
      retirementAge,
      lifeExpectancy,
      currentIncome,
      retirementIncome,
      filingStatus,
      currentState,
      retirementState,
      currentTaxAnalysis,
      retirementTaxAnalysis,
      conversionAnalysis,
      generateRangeAnalysis,
      optimizeConversionAmount,
      updatePersonalInfo,
      updateConversionParameters
    } = useRothCalculations();

    // Initialize from props data
    onMounted(() => {
      initializeFromProps();
    });

    const initializeFromProps = () => {
      // Extract retirement account balances from items
      const retirementAccounts = props.items.filter(item => 
        item.type === 'asset' && 
        (item.category === 'Investments' || item.name?.toLowerCase().includes('401k') || item.name?.toLowerCase().includes('ira'))
      );
      
      const totalRetirementBalance = retirementAccounts.reduce((sum, account) => {
        return sum + (account.amount || 0);
      }, 0);
      
      if (totalRetirementBalance > 0) {
        updateConversionParameters('currentBalance', totalRetirementBalance);
      }

      // Extract personal info
      if (props.personalInfo?.primary) {
        const primary = props.personalInfo.primary;
        if (primary.dateOfBirth) {
          const birthYear = new Date(primary.dateOfBirth).getFullYear();
          const age = new Date().getFullYear() - birthYear;
          updatePersonalInfo('currentAge', age);
        }
        if (primary.targetRetirementAge) {
          updatePersonalInfo('retirementAge', primary.targetRetirementAge);
        }
        if (primary.lifeExpectancy) {
          updatePersonalInfo('lifeExpectancy', primary.lifeExpectancy);
        }
      }

      // Extract income from cash flow items
      const incomeItems = props.items.filter(item => item.type === 'income');
      const totalIncome = incomeItems.reduce((sum, item) => sum + (item.amount || 0), 0);
      if (totalIncome > 0) {
        updatePersonalInfo('currentIncome', totalIncome);
        // Assume 70% income replacement in retirement
        updatePersonalInfo('retirementIncome', totalIncome * 0.7);
      }
    };

    // Quick analysis computations
    const defaultConversionAmount = computed(() => {
      return Math.min(currentBalance.value * 0.1, 50000); // 10% or $50k, whichever is smaller
    });

    const currentEffectiveRate = computed(() => {
      return currentTaxAnalysis.value?.effectiveRate || 0.22;
    });

    const retirementEffectiveRate = computed(() => {
      return retirementTaxAnalysis.value?.effectiveRate || 0.20;
    });

    const rateDifference = computed(() => {
      return retirementEffectiveRate.value - currentEffectiveRate.value;
    });

    const rateDifferenceClass = computed(() => {
      if (rateDifference.value > 0.01) return 'favorable';
      if (rateDifference.value < -0.01) return 'unfavorable';
      return 'neutral';
    });

    const estimatedBenefit = computed(() => {
      // Quick estimate: if retirement rate is higher, conversion likely beneficial
      if (rateDifference.value > 0) {
        return defaultConversionAmount.value * rateDifference.value * 1.5; // Growth factor
      }
      return defaultConversionAmount.value * rateDifference.value * 0.8; // Cost factor
    });

    const estimatedTaxCost = computed(() => {
      return defaultConversionAmount.value * currentEffectiveRate.value;
    });

    const benefitClass = computed(() => {
      return estimatedBenefit.value > 0 ? 'positive' : 'negative';
    });

    const quickDecisionClass = computed(() => {
      if (rateDifference.value > 0.02) return 'decision-good';
      if (rateDifference.value > -0.02) return 'decision-maybe';
      return 'decision-bad';
    });

    const quickDecisionIcon = computed(() => {
      if (rateDifference.value > 0.02) return '🚀';
      if (rateDifference.value > -0.02) return '⚖️';
      return '🛑';
    });

    const quickDecisionTitle = computed(() => {
      if (rateDifference.value > 0.02) return 'Roth Conversions Look Promising';
      if (rateDifference.value > -0.02) return 'Conversions Worth Considering';
      return 'Conversions May Not Be Optimal';
    });

    const quickDecisionDescription = computed(() => {
      if (rateDifference.value > 0.02) {
        return 'Your retirement tax rate is expected to be higher than today. Conversions likely beneficial.';
      } else if (rateDifference.value > -0.02) {
        return 'Tax rates are similar. Consider other factors like RMD reduction and estate planning.';
      } else {
        return 'Your current tax rate is higher than expected retirement rate. Conversions may be costly.';
      }
    });

    // Generate quick range data for simplified analysis
    const quickRangeData = computed(() => {
      const data = [];
      const maxAmount = Math.min(currentBalance.value, 150000);
      
      for (let amount = 0; amount <= maxAmount; amount += 10000) {
        const taxCost = amount * currentEffectiveRate.value;
        const benefit = amount * rateDifference.value * 1.2; // Simplified benefit calculation
        const netBenefit = benefit - taxCost;
        const marginalRate = currentEffectiveRate.value + (amount > 50000 ? 0.05 : 0); // Simplified bracket effect
        
        data.push({
          conversionAmount: amount,
          taxCost,
          benefit: Math.max(benefit, 0),
          netBenefit,
          marginalRate
        });
      }
      
      return data;
    });

    // Methods
    const runQuickOptimization = async () => {
      try {
        const result = optimizeConversionAmount('bracket');
        if (result) {
          // Update the default amount based on optimization
          updateConversionParameters('conversionAmount', result.amount);
          
          // Show a simple notification or update the UI
          console.log('Optimization complete:', result);
        }
      } catch (error) {
        console.error('Quick optimization failed:', error);
      }
    };

    const updateQuickAmount = (amount) => {
      updateConversionParameters('conversionAmount', amount);
    };

    const formatPercentage = (value) => {
      return `${(value * 100).toFixed(1)}%`;
    };

    return {
      showWizard,
      showRangeAnalysis,
      currentBalance,
      defaultConversionAmount,
      currentEffectiveRate,
      retirementEffectiveRate,
      rateDifference,
      rateDifferenceClass,
      estimatedBenefit,
      estimatedTaxCost,
      benefitClass,
      quickDecisionClass,
      quickDecisionIcon,
      quickDecisionTitle,
      quickDecisionDescription,
      quickRangeData,
      runQuickOptimization,
      updateQuickAmount,
      formatPercentage
    };
  }
};
</script>

<style scoped>
.roth-conversion-chart {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  margin: 2rem 0;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e9ecef;
}

.chart-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.5rem;
}

/* Quick Preview */
.quick-preview {
  margin-bottom: 2rem;
}

.preview-cards {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.preview-card {
  background: white;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;
}

.preview-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.preview-card.decision-good {
  border-color: #28a745;
  background: linear-gradient(135deg, #f8fff9 0%, #e8f5e9 100%);
}

.preview-card.decision-maybe {
  border-color: #ffc107;
  background: linear-gradient(135deg, #fffbf0 0%, #fff3cd 100%);
}

.preview-card.decision-bad {
  border-color: #dc3545;
  background: linear-gradient(135deg, #fff5f5 0%, #ffebee 100%);
}

.preview-card.info {
  border-color: #17a2b8;
  background: linear-gradient(135deg, #f0fcff 0%, #e1f7fa 100%);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.card-icon {
  font-size: 2.5rem;
}

.card-title h4 {
  margin: 0 0 0.25rem 0;
  color: #2c3e50;
  font-size: 1.2rem;
}

.card-title p {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
}

.card-metrics {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.metric {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 6px;
}

.metric-label {
  color: #6c757d;
  font-size: 0.9rem;
}

.metric-value {
  font-weight: 600;
  color: #2c3e50;
}

.metric-value.positive {
  color: #28a745;
}

.metric-value.negative {
  color: #dc3545;
}

/* Tax Rate Comparison */
.tax-rate-comparison {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.rate-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 6px;
}

.rate-item.difference {
  font-weight: 600;
  border: 1px solid transparent;
}

.rate-item.difference.favorable {
  border-color: #28a745;
  background: rgba(40, 167, 69, 0.1);
}

.rate-item.difference.unfavorable {
  border-color: #dc3545;
  background: rgba(220, 53, 69, 0.1);
}

.rate-item.difference.neutral {
  border-color: #6c757d;
  background: rgba(108, 117, 125, 0.1);
}

.rate-label {
  color: #6c757d;
  font-size: 0.9rem;
}

.rate-value {
  font-weight: 600;
  color: #2c3e50;
}

/* Quick Actions */
.quick-actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.action-item {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
  transition: all 0.3s ease;
}

.action-item:hover {
  background: #e9ecef;
  border-color: #ced4da;
  transform: translateY(-2px);
}

.action-item h5 {
  margin: 0 0 0.5rem 0;
  color: #495057;
  font-size: 1rem;
}

.action-item p {
  margin: 0 0 1rem 0;
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
}

/* Range Analysis Section */
.range-analysis-section {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.section-header h4 {
  margin: 0;
  color: #495057;
  font-size: 1.2rem;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;
  transition: color 0.2s ease;
}

.btn-close:hover {
  color: #495057;
}

/* Wizard Modal */
.wizard-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
}

.modal-content {
  position: relative;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 95vw;
  max-height: 95vh;
  overflow: auto;
  z-index: 1001;
}

.modal-close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 1002;
}

.modal-close-btn:hover {
  background: white;
  color: #495057;
  transform: scale(1.1);
}

/* Integration Info */
.integration-info {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
}

.integration-info details {
  cursor: pointer;
}

.integration-info summary {
  color: #6c757d;
  font-size: 0.9rem;
  font-weight: 500;
  padding: 0.5rem;
  list-style: none;
  outline: none;
}

.integration-info summary::-webkit-details-marker {
  display: none;
}

.info-content {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  margin-top: 0.5rem;
}

.info-content p {
  margin: 0 0 1rem 0;
  color: #495057;
  font-size: 0.9rem;
  line-height: 1.5;
}

.info-content h6 {
  margin: 1rem 0 0.5rem 0;
  color: #495057;
  font-size: 0.9rem;
  font-weight: 600;
}

.info-content ul {
  margin: 0;
  padding-left: 1.25rem;
  color: #6c757d;
  font-size: 0.85rem;
  line-height: 1.4;
}

.info-content li {
  margin-bottom: 0.25rem;
}

/* Buttons */
.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  text-decoration: none;
  display: inline-block;
  text-align: center;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover {
  background: #0056b3;
  transform: translateY(-1px);
}

.btn-outline {
  background: white;
  color: #007bff;
  border: 2px solid #007bff;
}

.btn-outline:hover {
  background: #007bff;
  color: white;
  transform: translateY(-1px);
}

.btn-accent {
  background: #17a2b8;
  color: white;
}

.btn-accent:hover {
  background: #138496;
  transform: translateY(-1px);
}

/* Responsive */
@media (max-width: 768px) {
  .roth-conversion-chart {
    padding: 1rem;
    margin: 1rem 0;
  }
  
  .chart-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .preview-cards {
    grid-template-columns: 1fr;
  }
  
  .quick-actions {
    grid-template-columns: 1fr;
  }
  
  .card-header {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
  
  .wizard-modal {
    padding: 0.5rem;
  }
  
  .modal-content {
    max-width: 100vw;
    border-radius: 8px;
  }
}
</style>